
/* ==========================================================================
   STANDALONE GIANT IMAGE GALLERY MODAL (/database/js/gallery-modal.js)
   ========================================================================== */

import { escapeHtml, formatCaseBrandTitle, slugifyUrlSegment, caseRouteSlug, sanitizeUrl } from '../utils.js?v=20260819-audit2';
import { fetchCaseCommunityBuilds } from '../api.js?v=20260819-phase35';
import { activateAccessibleLayer, deactivateAccessibleLayer } from '../a11y.js?v=20260819-audit2';
import { debugRuntimeIssue } from '../runtime.js?v=20260819-audit2';
import { getDocumentedBuildFilterOptions, documentedBuildMatchesFilters } from './ui-helpers.js?v=20260819-audit2';

function sanitizeRawUrl(url) {
  if (!url) return '#';
  const raw = String(url).trim();
  if (!raw) return '#';

  const hasExplicitScheme = /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(raw);
  const isProtocolRelative = raw.startsWith('//');

  try {
    const baseOrigin = (typeof window !== 'undefined' && window.location?.origin)
      ? window.location.origin
      : 'https://sff.gg';
    const parsed = new URL(raw, baseOrigin);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '#';

    if (!hasExplicitScheme && !isProtocolRelative) {
      if (parsed.origin !== baseOrigin) return '#';
      return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/';
    }

    return parsed.href;
  } catch (err) {
    debugRuntimeIssue('Gallery URL', err);
    return '#';
  }
}


export class GalleryModal {
  static caseObj = null;
  static allImages = [];
  static activeCategory = 'all'; // 'all', 'editorial', 'community'
  static activeImageIndex = 0;
  static currentPage = 1;
  static itemsPerPage = 21;
  static activeBuildFilters = {};
  static buildFilterDimensions = [];
  static buildFilterDescriptors = [];
  static buildCount = 0;

  /**
   * Initializes and opens the isolated gallery modal for a case
   */
  static async open(caseObj) {
    if (!caseObj) return;

    this.caseObj = caseObj;
    this.activeImageIndex = 0;
    this.currentPage = 1;
    this.activeCategory = 'all';
    this.activeBuildFilters = {};
    this.buildFilterDimensions = [];
    this.buildFilterDescriptors = [];
    this.buildCount = 0;

    const caseSlug = caseRouteSlug(caseObj);

    // Build base image collection with official/editorial image
    this.allImages = [
      {
        id: 'official-main',
        full_url: sanitizeRawUrl(caseObj.image),
        thumb_url: sanitizeRawUrl(caseObj.image),
        title: `${formatCaseBrandTitle(caseObj.name)} Official Render`,
        source: 'Editorial',
        builder: 'SFFLABS',
        type: 'editorial'
      }
    ];

    // Fetch review and build photos via API (including Cornerstone inline photos).
    // Build filters are derived only from the documented builds returned for this case.
    const apiData = await fetchCaseCommunityBuilds(caseSlug, 1, 50);
    if (apiData) {
      const builds = Array.isArray(apiData.builds) ? apiData.builds : [];
      const filterModel = getDocumentedBuildFilterOptions(builds, caseObj);
      this.buildFilterDimensions = filterModel.dimensions;
      this.buildFilterDescriptors = filterModel.descriptors;
      this.buildCount = builds.length;

      // gallery_photos is the merged Editorial + Community photo stream. Map each
      // Community photo back to its parent build by canonical image URL so the
      // gallery can filter photos without another API request or backend schema.
      const buildFilterByPhotoUrl = new Map();
      builds.forEach((build, buildIndex) => {
        const descriptor = filterModel.descriptors[buildIndex] || {};
        (Array.isArray(build.photos) ? build.photos : []).forEach(photo => {
          const fullUrl = sanitizeRawUrl(photo?.full_url || photo?.thumb_url);
          if (fullUrl && fullUrl !== '#') buildFilterByPhotoUrl.set(fullUrl, descriptor);
        });
      });

      if (Array.isArray(apiData.gallery_photos) && apiData.gallery_photos.length > 0) {
        apiData.gallery_photos.forEach(photo => {
          const fullUrl = sanitizeRawUrl(photo.full_url);
          const type = photo.type || 'community';
          this.allImages.push({
            id: photo.id || `photo-${Math.random().toString(36).substring(2, 7)}`,
            full_url: fullUrl,
            thumb_url: sanitizeRawUrl(photo.thumb_url),
            title: photo.title || formatCaseBrandTitle(caseObj.name),
            source: photo.source || (type === 'editorial' ? 'Editorial' : 'Community Build'),
            builder: photo.builder || 'Enthusiast',
            type,
            build_filters: type === 'community' ? (buildFilterByPhotoUrl.get(fullUrl) || {}) : {}
          });
        });
      } else if (builds.length) {
        builds.forEach((build, buildIndex) => {
          const descriptor = filterModel.descriptors[buildIndex] || {};
          if (Array.isArray(build.photos) && build.photos.length > 0) {
            build.photos.forEach((photo, idx) => {
              this.allImages.push({
                id: `build-${build.build_id}-${idx}`,
                full_url: sanitizeRawUrl(photo.full_url),
                thumb_url: sanitizeRawUrl(photo.thumb_url),
                title: build.title,
                source: 'Community Build',
                builder: build.builder,
                gpu: build.gpu,
                cpu: build.cpu,
                date: build.date,
                permalink: sanitizeRawUrl(build.permalink),
                type: 'community',
                build_filters: descriptor
              });
            });
          }
        });
      }
    }

    this.ensureModalContainer();
    this.renderModal();
  }

  /**
   * Generates a completely isolated #sffGalleryModal element in document.body
   */
  static ensureModalContainer() {
    let modal = document.getElementById('sffGalleryModal');
    if (!modal) {
      modal = document.createElement('div');
      modal.id = 'sffGalleryModal';
      modal.className = 'sff-modal-backdrop font-mono';
      modal.style.display = 'none';
      document.body.appendChild(modal);
    }
  }

  /**
   * Filtered images based on active tab selection
   */
  static getFilteredImages() {
    if (this.activeCategory === 'editorial') {
      return this.allImages.filter(img => img.type === 'editorial');
    }
    if (this.activeCategory === 'community') {
      return this.allImages.filter(img => (
        img.type === 'community' && documentedBuildMatchesFilters(img.build_filters || {}, this.activeBuildFilters)
      ));
    }
    return this.allImages;
  }

  static buildFilterBarHtml() {
    if (this.activeCategory !== 'community' || this.buildCount < 2 || this.buildFilterDimensions.length === 0) return '';

    const matchingBuilds = this.buildFilterDescriptors.filter(descriptor => (
      documentedBuildMatchesFilters(descriptor, this.activeBuildFilters)
    )).length;
    const matchingPhotos = this.getFilteredImages().length;

    return `
      <div class="sff-build-filter-bar gallery-build-filter-bar" data-gallery-build-filter-root>
        <div class="sff-build-filter-head">
          <span class="sff-build-filter-title">FILTER DOCUMENTED BUILDS</span>
          <span class="sff-build-filter-status">SHOWING ${matchingBuilds} / ${this.buildCount} BUILDS · ${matchingPhotos} PHOTOS</span>
          <button type="button" class="sff-build-filter-clear" data-gallery-build-filter-clear>CLEAR</button>
        </div>
        <div class="sff-build-filter-grid">
          ${this.buildFilterDimensions.map(dimension => `
            <label class="sff-build-filter-field">
              <span>${escapeHtml(dimension.label)}</span>
              <select class="sff-build-filter-select" data-gallery-build-filter-key="${escapeHtml(dimension.key)}" aria-label="Filter build gallery by ${escapeHtml(dimension.label.toLowerCase())}">
                <option value="all">ALL (${this.buildCount})</option>
                ${dimension.options.map(option => `
                  <option value="${escapeHtml(option.value)}" ${String(this.activeBuildFilters[dimension.key] || 'all') === String(option.value) ? 'selected' : ''}>${escapeHtml(option.label)} (${option.count})</option>
                `).join('')}
              </select>
            </label>
          `).join('')}
        </div>
      </div>`;
  }

  /**
   * Builds and injects clean gallery markup without touching #sffReportModal
   */
  static renderModal() {
    const modal = document.getElementById('sffGalleryModal');
    if (!modal) return;

    const filtered = this.getFilteredImages();
    const caseTitle = formatCaseBrandTitle(this.caseObj.name);
    const totalPages = Math.max(1, Math.ceil(filtered.length / this.itemsPerPage));

    modal.innerHTML = `
      <div class="sff-modal-container gallery-modal-container">
        <!-- ISOLATED TOPBAR -->
        <div class="sff-modal-topbar font-mono">
          <div class="gallery-topbar-title">
            <span id="sffGalleryTitle" class="gallery-case-title">${escapeHtml(caseTitle)}</span>
            <span class="gallery-topbar-meta">// MASTER GALLERY (${filtered.length} PHOTOS)</span>
          </div>

          <div class="sff-modal-top-actions">
            <div class="gallery-tab-group">
              <button type="button" class="gallery-tab-btn ${this.activeCategory === 'all' ? 'active' : ''}" data-cat="all" aria-pressed="${this.activeCategory === 'all' ? 'true' : 'false'}">ALL (${this.allImages.length})</button>
              <button type="button" class="gallery-tab-btn ${this.activeCategory === 'editorial' ? 'active' : ''}" data-cat="editorial" aria-pressed="${this.activeCategory === 'editorial' ? 'true' : 'false'}">EDITORIAL</button>
              <button type="button" class="gallery-tab-btn ${this.activeCategory === 'community' ? 'active' : ''}" data-cat="community" aria-pressed="${this.activeCategory === 'community' ? 'true' : 'false'}">BUILDS</button>
            </div>
            <button type="button" class="sff-modal-close-icon" id="galleryCloseX" aria-label="Close Gallery">&times;</button>
          </div>
        </div>

        ${this.buildFilterBarHtml()}

        <!-- ISOLATED BODY WORKSPACE -->
        <div class="sff-modal-body gallery-modal-body">
          ${this.buildWorkspaceHtml(filtered)}
        </div>

        <!-- ISOLATED FOOTER -->
        <div class="sff-modal-footer font-mono">
          <div class="sff-footer-status">
            <span class="gallery-footer-page">GALLERY PAGE ${this.currentPage} OF ${totalPages}</span>
            <span class="sff-status-pill completed">${filtered.length} PHOTOS</span>
          </div>
          <div class="sff-footer-actions">
            <button type="button" class="sff-action-pill" id="galleryPrevPage" ${this.currentPage <= 1 ? 'disabled style="opacity:0.4;cursor:not-allowed;"' : ''}>&lsaquo; PREV PAGE</button>
            <button type="button" class="sff-action-pill" id="galleryNextPage" ${this.currentPage >= totalPages ? 'disabled style="opacity:0.4;cursor:not-allowed;"' : ''}>NEXT PAGE &rsaquo;</button>
            <button type="button" class="sff-modal-btn-close" id="galleryCloseFooter">CLOSE</button>
          </div>
        </div>
      </div>
    `;

    this.bindEvents(modal);

    modal.classList.add('active');
    modal.style.display = 'flex';
    activateAccessibleLayer(modal, {
      label: `${caseTitle} image gallery`,
      initialFocusSelector: '#galleryCloseX',
      onClose: () => this.close()
    });
  }

  /**
   * Builds Hero stage + Sprawling Thumbnail Grid
   */
  static buildWorkspaceHtml(filtered) {
    if (!filtered.length) {
      return `
        <div class="gallery-workspace font-mono" style="justify-content:center; align-items:center; text-align:center; padding:40px;">
          <span class="gallery-empty-message">NO PHOTOS AVAILABLE IN THIS CATEGORY</span>
        </div>`;
    }

    const currentImg = filtered[this.activeImageIndex] || filtered[0];

    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const pageItems = filtered.slice(startIndex, startIndex + this.itemsPerPage);

    return `
      <div class="gallery-workspace font-mono">
        <!-- HERO VIEWPORT -->
        <div class="gallery-hero-stage">
          <button type="button" class="gallery-hero-nav gallery-hero-prev" id="galleryHeroPrev" title="Previous photo" aria-label="Previous photo">&lsaquo;</button>
          <img src="${sanitizeUrl(currentImg.full_url)}" alt="${escapeHtml(currentImg.title)}" class="gallery-hero-img" id="galleryHeroImg" onerror="this.onerror=null;this.src='/assets/images/placeholder.webp';" />
          <button type="button" class="gallery-hero-nav gallery-hero-next" id="galleryHeroNext" title="Next photo" aria-label="Next photo">&rsaquo;</button>

          <div class="gallery-hero-overlay">
            <div class="gallery-hero-info">
              <h3 class="gallery-hero-title" id="galleryHeroTitle">${escapeHtml(currentImg.title)}</h3>
              <div class="gallery-hero-meta" id="galleryHeroMeta">
                <span class="audit-pill pill-sky">${escapeHtml(currentImg.source)}</span>
                <span>BY ${escapeHtml(currentImg.builder)}</span>
                ${currentImg.gpu ? `<span>· GPU: ${escapeHtml(currentImg.gpu)}</span>` : ''}
              </div>
            </div>
            <a href="${sanitizeUrl(currentImg.full_url)}" target="_blank" rel="noopener noreferrer" id="galleryHeroFullLink" class="sff-action-pill" style="text-decoration:none;" title="Open full resolution photo in new tab">FULL RES ↗</a>
          </div>
        </div>

        <!-- SPRAWLING THUMBNAIL GRID (21 ITEMS PER PAGE) -->
        <div class="gallery-grid-viewport">
          <div class="gallery-sprawling-grid">
            ${pageItems.map((img, idx) => {
              const globalIdx = startIndex + idx;
              const isActive = globalIdx === this.activeImageIndex;
              return `
                <button type="button" class="gallery-thumb-card ${isActive ? 'active' : ''}" data-global-idx="${globalIdx}" aria-label="View ${escapeHtml(img.title)}" aria-pressed="${isActive ? 'true' : 'false'}">
                  <img src="${sanitizeUrl(img.thumb_url)}" alt="${escapeHtml(img.title)}" />
                  <span class="gallery-thumb-badge">${img.type === 'editorial' ? 'ED' : 'BUILD'}</span>
                </button>
              `;
            }).join('')}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Binds click handlers strictly inside #sffGalleryModal
   */
  static bindEvents(modal) {
    // Category Tabs
    modal.querySelectorAll('.gallery-tab-btn').forEach(btn => {
      btn.onclick = (e) => {
        e.preventDefault();
        this.activeCategory = btn.getAttribute('data-cat') || 'all';
        this.activeImageIndex = 0;
        this.currentPage = 1;
        this.renderModal();
      };
    });

    // Community-build filters. They intentionally appear only on the BUILDS
    // category so selecting a hardware filter never hides Editorial media.
    modal.querySelectorAll('[data-gallery-build-filter-key]').forEach(select => {
      select.onchange = () => {
        const key = select.getAttribute('data-gallery-build-filter-key');
        if (!key) return;
        this.activeBuildFilters[key] = select.value || 'all';
        this.activeImageIndex = 0;
        this.currentPage = 1;
        this.renderModal();
      };
    });

    const clearBuildFilters = modal.querySelector('[data-gallery-build-filter-clear]');
    if (clearBuildFilters) {
      clearBuildFilters.onclick = (e) => {
        e.preventDefault();
        this.activeBuildFilters = {};
        this.activeImageIndex = 0;
        this.currentPage = 1;
        this.renderModal();
      };
    }

    // Hero Navigation Arrows
    const prevHero = document.getElementById('galleryHeroPrev');
    const nextHero = document.getElementById('galleryHeroNext');

    if (prevHero) {
      prevHero.onclick = (e) => {
        e.preventDefault();
        const filtered = this.getFilteredImages();
        this.activeImageIndex = (this.activeImageIndex - 1 + filtered.length) % filtered.length;
        this.syncHeroView(filtered);
      };
    }

    if (nextHero) {
      nextHero.onclick = (e) => {
        e.preventDefault();
        const filtered = this.getFilteredImages();
        this.activeImageIndex = (this.activeImageIndex + 1) % filtered.length;
        this.syncHeroView(filtered);
      };
    }

    // Thumbnail Selection
    modal.querySelectorAll('.gallery-thumb-card').forEach(card => {
      card.onclick = (e) => {
        e.preventDefault();
        const globalIdx = parseInt(card.getAttribute('data-global-idx'), 10);
        this.activeImageIndex = globalIdx;
        const filtered = this.getFilteredImages();
        this.syncHeroView(filtered);
      };
    });

    // Pagination Controls
    const prevPage = document.getElementById('galleryPrevPage');
    const nextPage = document.getElementById('galleryNextPage');
    const closeX = document.getElementById('galleryCloseX');
    const closeFooter = document.getElementById('galleryCloseFooter');

    const closeModal = (e) => {
      if (e) e.preventDefault();
      this.close();
    };

    if (prevPage) {
      prevPage.onclick = (e) => {
        e.preventDefault();
        if (this.currentPage > 1) {
          this.currentPage--;
          this.renderModal();
        }
      };
    }

    if (nextPage) {
      nextPage.onclick = (e) => {
        e.preventDefault();
        const filtered = this.getFilteredImages();
        const totalPages = Math.ceil(filtered.length / this.itemsPerPage);
        if (this.currentPage < totalPages) {
          this.currentPage++;
          this.renderModal();
        }
      };
    }

    if (closeX) closeX.onclick = closeModal;
    if (closeFooter) closeFooter.onclick = closeModal;

    modal.onclick = (e) => {
      if (e.target === modal) closeModal(e);
    };
  }

  static close() {
    const modal = document.getElementById('sffGalleryModal');
    if (!modal) return;
    deactivateAccessibleLayer(modal);
    modal.classList.remove('active');
    modal.style.display = 'none';
  }

  /**
   * Syncs hero stage view without rebuilding the whole grid
   */
  static syncHeroView(filtered) {
    const currentImg = filtered[this.activeImageIndex];
    if (!currentImg) return;

    const imgEl = document.getElementById('galleryHeroImg');
    const titleEl = document.getElementById('galleryHeroTitle');
    const metaEl = document.getElementById('galleryHeroMeta');
    const linkEl = document.getElementById('galleryHeroFullLink');

    if (imgEl) imgEl.src = sanitizeRawUrl(currentImg.full_url);
    if (titleEl) titleEl.innerText = currentImg.title;
    if (linkEl) linkEl.href = sanitizeRawUrl(currentImg.full_url);

    if (metaEl) {
      metaEl.innerHTML = `
        <span class="audit-pill pill-sky">${escapeHtml(currentImg.source)}</span>
        <span>BY ${escapeHtml(currentImg.builder)}</span>
        ${currentImg.gpu ? `<span>· GPU: ${escapeHtml(currentImg.gpu)}</span>` : ''}
      `;
    }

    document.querySelectorAll('.gallery-thumb-card').forEach(card => {
      const gIdx = parseInt(card.getAttribute('data-global-idx'), 10);
      const isActive = gIdx === this.activeImageIndex;
      card.classList.toggle('active', isActive);
      card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }
}
